import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { CHART } from "@/constants/testIds";

// Illustrative: a hypothetical equity scheme outperforming the index after research-based selection.
// Annualised growth applied to ₹1,00,000 over 10 years.
function buildSeries(indexCAGR, schemeCAGR, years = 10, start = 100000) {
  const out = [];
  for (let y = 0; y <= years; y++) {
    out.push({
      year: `Y${y}`,
      Index: Math.round(start * Math.pow(1 + indexCAGR, y)),
      Scheme: Math.round(start * Math.pow(1 + schemeCAGR, y)),
    });
  }
  return out;
}

const SCENARIOS = {
  largecap: { label: "Large Cap", index: 0.115, scheme: 0.142 },
  flexicap: { label: "Flexi Cap", index: 0.121, scheme: 0.165 },
  midcap: { label: "Mid Cap", index: 0.155, scheme: 0.198 },
};

export default function PerformanceChart() {
  const [scenario, setScenario] = useState("flexicap");
  const data = useMemo(
    () => buildSeries(SCENARIOS[scenario].index, SCENARIOS[scenario].scheme),
    [scenario]
  );
  const last = data[data.length - 1];
  const delta = last.Scheme - last.Index;

  return (
    <section data-testid={CHART.section} id="research-example" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
      <div className="grid md:grid-cols-12 gap-10 mb-12">
        <div className="md:col-span-6">
          <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium">
            Research, in one chart
          </span>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
            Index vs. <em className="not-italic text-secondary">research-picked</em> scheme.
          </h2>
          <p className="mt-5 text-foreground/75 leading-relaxed">
            One of the questions we ask before recommending any fund:{" "}
            <em className="text-primary not-italic font-medium">"Has it consistently beaten its benchmark?"</em>{" "}
            Below is an illustrative comparison of how a research-shortlisted scheme has performed
            against its benchmark index over 10 years on a ₹1,00,000 investment.
          </p>
        </div>
        <div className="md:col-span-6 md:pl-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(SCENARIOS).map(([key, val]) => (
              <button
                key={key}
                onClick={() => setScenario(key)}
                data-testid={`${CHART.toggle}-${key}`}
                className={`text-xs uppercase tracking-wider px-4 py-2 rounded-full border transition-all ${
                  scenario === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-foreground/70 hover:border-primary/40"
                }`}
              >
                {val.label}
              </button>
            ))}
          </div>
          <div className="bg-card rounded-2xl p-4 md:p-6 border border-border">
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="g-scheme" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(155 30% 25%)" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="hsl(155 30% 25%)" stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="g-index" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(24 45% 55%)" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="hsl(24 45% 55%)" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 20% 80%)" />
                  <XAxis dataKey="year" stroke="hsl(0 0% 45%)" fontSize={12} />
                  <YAxis stroke="hsl(0 0% 45%)" fontSize={12} tickFormatter={(v) => `${(v / 100000).toFixed(1)}L`} />
                  <Tooltip
                    formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                    contentStyle={{ borderRadius: 12, border: "1px solid hsl(40 20% 85%)" }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="Index" stroke="hsl(24 45% 55%)" strokeWidth={2} fill="url(#g-index)" />
                  <Area type="monotone" dataKey="Scheme" stroke="hsl(155 30% 25%)" strokeWidth={2.5} fill="url(#g-scheme)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <motion.div
              key={scenario}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 grid grid-cols-3 gap-3 text-center"
            >
              <div className="rounded-xl bg-muted/60 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Index value</div>
                <div className="font-serif text-lg text-primary mt-1">₹{(last.Index / 100000).toFixed(2)}L</div>
              </div>
              <div className="rounded-xl bg-primary/5 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Scheme value</div>
                <div className="font-serif text-lg text-primary mt-1">₹{(last.Scheme / 100000).toFixed(2)}L</div>
              </div>
              <div className="rounded-xl bg-secondary/10 p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Alpha</div>
                <div className="font-serif text-lg text-secondary mt-1">+₹{(delta / 100000).toFixed(2)}L</div>
              </div>
            </motion.div>
            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
              * Illustrative figures for educational purposes. Actual scheme performance varies. Past
              performance is not a guarantee of future returns.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
